import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import isEqual from "lodash/isEqual";
import mixpanel from "mixpanel-browser";

import { useStateSelector } from "utils/field";
import { timeDifference } from "utils";
import { hasInsightsAccess } from "utils/insights";

import { cloneDeep } from "lodash";

import { change } from "store/root/actions";
import { saveModel } from "features/Model/_ducks/thunk";
import { saveHybridAnalysis } from "features/Insights/HybridHeating/hybridAnalysis";

import { useInsights } from "../InsightsContext";
import InsightsTag from "../InsightsTag";
import ReviewProgressIndicator from "features/Model/ModelTopBar/ReviewProgressIndicator";

import Button from "components/Button";
import { useSimulationCompletion } from "components/ModelProgressBar";

import InsightsHeaderTooltips from "./InsightsHeaderTooltips";

import ArrowDownBlue from "~assets/images/icons/JSX/arrow-down-blue";
import Save from "assets/images/icons/JSX/save";
import SettingsIcon from "assets/images/icons/JSX/Settings";
import VersionHistoryIcon from "assets/images/icons/JSX/VersionHistory";

import classes from "./styles.module.scss";

const InsightsTopBar = ({ isCollapsed }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { setSettingsOpen, setVersionHistoryOpen, submitting, setSubmitting } = useInsights();

    const [savingText, setSavingText] = useState("Save Changes");
    const [saveTooltipText, setSaveTooltipText] = useState("Save model");
    const [wasSaving, setWasSaving] = useState(false);

    const { uid } = useStateSelector("user");
    const model = useStateSelector("model") || {};
    const {
        modelId = null,
        modelData = {},
        modelDetails: {
            name = "",
            lastEdited = {},
            owner = {},
            writePermission = [],
            simulationStatus = "IDLE",
            isSimulationRunning: simulationRunning,
            isInsightsActive = false,
            createdAt = "",
        } = {},
        modelDataToCompare = {},
        modelDetailsToCompare = {},
        modelTakeoff = {},
        modelTakeoffToCompare = {},
        linkedBaseline = {},
        linkedBaselineToCompare = {},
        dataEntryMode = "",
        dataEntryModeToCompare = "",
    } = model;
    const hasAccess = hasInsightsAccess({ isInsightsActive, createdAt });
    const { simulationCompletion, attentionChecks, modelComplete, hasSimulationResults } = useSimulationCompletion();
    const { packages: upgradePackages = {}, initPackages: initUpgradePackages = {} } = useStateSelector("upgrades");

    const ownerId = owner?.uid || uid;
    const readOnly = ownerId !== uid && !writePermission.includes(uid);
    const isSimulationRunning = simulationStatus === "RUNNING" || simulationRunning;

    useEffect(() => {
        if (readOnly) {
            setSavingText("Read-only");
            setSaveTooltipText("Saving disabled in read-only mode");
            return;
        }

        if (submitting) {
            setSavingText("Saving...");
            setSaveTooltipText("Saving...");
            setWasSaving(true);
        } else if (wasSaving) {
            setSavingText("Changes Saved");
            setSaveTooltipText("There are no changes to save");
            const timeout = setTimeout(() => {
                setSavingText("Save Changes");
                setWasSaving(false);
            }, 2000);
            return () => clearTimeout(timeout);
        } else if (isSimulationRunning) {
            setSavingText("Simulation Running");
        }

        if (!isDataChanged || isSimulationRunning) {
            setSaveTooltipText("There are no changes to save");
        }
    }, [submitting, readOnly]);

    const hybridAnalysis = model.insightsData?.hybridAnalysis;
    const savedHybridAnalysis = model.insightsDataSaved?.hybridAnalysis;
    const isDataChanged =
        !isEqual(modelData, modelDataToCompare) ||
        !isEqual({ ...modelDetailsToCompare, lastEdited: {} }, { ...model.modelDetails, lastEdited: {} }) ||
        !isEqual(upgradePackages, initUpgradePackages) ||
        !isEqual(modelTakeoffToCompare, modelTakeoff) ||
        !isEqual(linkedBaselineToCompare, linkedBaseline) ||
        (dataEntryMode ?? "").toLowerCase() !== (dataEntryModeToCompare ?? "").toLowerCase() ||
        (Boolean(hybridAnalysis) && !isEqual(hybridAnalysis, savedHybridAnalysis));

    useEffect(() => {
        if (isDataChanged) {
            const unloadCallback = (event) => {
                event.preventDefault();
                event.returnValue = "";
                return "";
            };

            window.addEventListener("beforeunload", unloadCallback);
            return () => window.removeEventListener("beforeunload", unloadCallback);
        }
    }, [isDataChanged]);

    const lastEditedDisplay = lastEdited?.datetime
        ? `Last edited ${timeDifference({ start: new Date(), end: lastEdited.datetime })} by ${lastEdited.user || owner.name || ""}.`
        : "";

    const handleSave = async () => {
        if (readOnly) return;
        mixpanel.track("Save Model");
        if (hybridAnalysis && modelId) {
            await saveHybridAnalysis(modelId, hybridAnalysis);
            dispatch(change("model.insightsDataSaved", { hybridAnalysis: cloneDeep(hybridAnalysis) }));
        }
        dispatch(saveModel(modelId, setSubmitting));
    };

    return (
        <div className={`${classes.insightsTopBarContainer} ${isCollapsed ? classes.collapsed : ""}`}>
            <div className={classes.insightsTopBarLeftContainer}>
                <div className={classes.backArrow} onClick={() => history.push(`/models/${modelId}`)}>
                    <ArrowDownBlue fill="#62BCF8" />
                </div>
                <div className={classes.insightsTopBarLeftContent}>
                    <div className={classes.insightsTopBarLeftContentTitle}>
                        <p>{name || "Model Name"}</p>
                        <InsightsTag isInsightsActive={hasAccess} />
                        <ReviewProgressIndicator
                            percentage={simulationCompletion}
                            checks={attentionChecks}
                            modelComplete={modelComplete}
                            hasSimulationResults={hasSimulationResults}
                        />
                    </div>
                    <span className={classes.insightsTopBarLeftContentLastEdited}>{lastEditedDisplay}</span>
                </div>
            </div>
            <div className={classes.insightsTopBarRightContainer}>
                <div data-for="versionHistoryTip" data-tip={"Version History"}>
                    <Button
                        smallPadding
                        type="emptyBg"
                        icon={VersionHistoryIcon}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                            mixpanel.track("Version History Open");
                            setVersionHistoryOpen(true);
                        }}
                    />
                </div>
                <div data-for="settingsTip" data-tip="Change your model's settings">
                    <Button
                        smallPadding
                        type="emptyBg"
                        icon={SettingsIcon}
                        onClick={() => {
                            mixpanel.track("Model Settings Open");
                            setSettingsOpen(true);
                        }}
                        className={classes.settingsButton}
                    />
                </div>
                <div data-for="saveTip" data-tip={saveTooltipText}>
                    <Button
                        smallPadding
                        icon={() => <Save strokeClass={classes.saveIconStroke} />}
                        onClick={handleSave}
                        disabled={isSimulationRunning || submitting || !isDataChanged || readOnly}
                        className={classes.minWidth}
                    >
                        {savingText}
                    </Button>
                </div>
            </div>
            <InsightsHeaderTooltips />
        </div>
    );
};

export default InsightsTopBar;
